import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserRoles, ROLES_GROUPS } from '../enums/user-roles.enum';
import { LoggerService } from './logger.service';

// Interface para el usuario actual
interface CurrentUser {
    id_usuario: number;
    username: string;
    rol_id: number;
    rol_nombre: string;
    rol_enum: UserRoles;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    // ===============================
    // PROPIEDADES PRIVADAS
    // ===============================

    private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
    private isLoggedInSubject = new BehaviorSubject<boolean>(false);

    // ===============================
    // OBSERVABLES PÚBLICOS
    // ===============================

    public currentUser$ = this.currentUserSubject.asObservable();
    public isLoggedIn$ = this.isLoggedInSubject.asObservable();

    constructor(private logger: LoggerService) {
        this.loadUserFromStorage();
    }

    // ===============================
    // MÉTODOS DE INICIALIZACIÓN
    // ===============================

    /**
     * Carga el usuario desde localStorage al inicializar
     */
    private loadUserFromStorage(): void {
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const userData = JSON.parse(userStr);
                const currentUser = this.transformUserData(userData);
                this.setCurrentUser(currentUser);
            }
        } catch (error) {
            this.logger.error('Error al cargar usuario desde localStorage:', error);
            this.logout();
        }
    }

    /**
     * Transforma los datos del usuario al formato interno
     */
    private transformUserData(userData: any): CurrentUser {
        const rolEnum = this.mapRolNombreToEnum(userData.rol_nombre || '');

        return {
            id_usuario: userData.id_usuario,
            username: userData.username,
            rol_id: userData.rol_id,
            rol_nombre: userData.rol_nombre,
            rol_enum: rolEnum
        };
    }

    /**
     * Mapea el nombre del rol al enum
     */
    private mapRolNombreToEnum(rolNombre: string): UserRoles {
        const mapping: { [key: string]: UserRoles } = {
            'Administrador': UserRoles.ADMIN,
            'Secretario': UserRoles.SECRETARIO,
            'Supervisor': UserRoles.SUPERVISOR,
            'Usuario': UserRoles.USUARIO
        };
        return mapping[rolNombre] || UserRoles.USUARIO;
    }

    // ===============================
    // MÉTODOS DE GESTIÓN DE USUARIO
    // ===============================

    /**
     * Establece el usuario actual (usado en login)
     */
    setCurrentUser(user: CurrentUser): void {
        this.currentUserSubject.next(user);
        this.isLoggedInSubject.next(true);
    }

    /**
     * Obtiene el usuario actual (sincrono)
     */
    getCurrentUser(): CurrentUser | null {
        return this.currentUserSubject.value;
    }

    /**
     * Obtiene el rol actual del usuario
     */
    getCurrentUserRole(): UserRoles | null {
        const user = this.getCurrentUser();
        return user ? user.rol_enum : null;
    }

    /**
     * Logout - limpia datos del usuario
     */
    logout(): void {
        localStorage.removeItem('user');
        this.currentUserSubject.next(null);
        this.isLoggedInSubject.next(false);
    }

    /**
     * Verifica si el usuario está logueado
     */
    isLoggedIn(): boolean {
        return this.isLoggedInSubject.value;
    }

    // ===============================
    // MÉTODOS DE VERIFICACIÓN DE PERMISOS (CENTRALIZADOS)
    // ===============================

    /**
     * Verifica si el usuario tiene un rol específico
     */
    hasRole(role: UserRoles): boolean {
        const currentRole = this.getCurrentUserRole();
        return currentRole === role;
    }

    /**
     * Verifica si el usuario tiene alguno de los roles especificados
     */
    hasAnyRole(roles: UserRoles[]): boolean {
        const currentRole = this.getCurrentUserRole();
        if (!currentRole) return false;
        return roles.includes(currentRole);
    }

    /**
     * Verifica si el usuario pertenece a un grupo de roles
     */
    hasRoleGroup(groupName: keyof typeof ROLES_GROUPS): boolean {
        const roles = ROLES_GROUPS[groupName];
        return this.hasAnyRole(roles);
    }

    /**
     * Verifica si es administrador
     */
    isAdmin(): boolean {
        return this.hasRole(UserRoles.ADMIN);
    }

    /**
     * Verifica si es secretario
     */
    isSecretario(): boolean {
        return this.hasRole(UserRoles.SECRETARIO);
    }

    /**
     * Verifica si es supervisor
     */
    isSupervisor(): boolean {
        return this.hasRole(UserRoles.SUPERVISOR);
    }

    /**
     * Verifica si es usuario básico
     */
    isUsuario(): boolean {
        return this.hasRole(UserRoles.USUARIO);
    }

    // ===============================
    // MÉTODOS DE PERMISOS ESPECÍFICOS (REUTILIZABLES)
    // ===============================

    /**
     * Puede gestionar usuarios (crear, editar, eliminar)
     */
    canManageUsers(): boolean {
        return this.hasRoleGroup('MANAGEMENT'); // Solo ADMIN
    }

    /**
     * Puede ver reportes
     */
    canViewReports(): boolean {
        return this.hasAnyRole([UserRoles.ADMIN]);
    }

    /**
     * Puede ver configuración del sistema
     */
    canViewSettings(): boolean {
        return this.hasRoleGroup('MANAGEMENT'); // Solo ADMIN
    }

    /**
     * Puede crear/editar facturas
     */
    canManageInvoices(): boolean {
        return this.hasAnyRole([UserRoles.ADMIN, UserRoles.SECRETARIO]);
    }

    canDeleteInvoices(): boolean {
        return this.isAdmin(); // Solo admin puede eliminar facturas
    }

    /**
     * Puede eliminar registros importantes
     */
    canDeleteImportantRecords(): boolean {
        return this.isAdmin(); // Solo admin puede eliminar cosas importantes
    }

    /**
     * Puede ver datos financieros
     */
    canViewFinancialData(): boolean {
        return this.hasAnyRole([UserRoles.ADMIN, UserRoles.SECRETARIO]);
    }

    /**
     * Puede exportar datos
     */
    canExportData(): boolean {
        return this.hasAnyRole([UserRoles.ADMIN, UserRoles.SECRETARIO, UserRoles.SUPERVISOR]);
    }

    canDeleteContracts(): boolean {
        return this.isAdmin();
    }
    // ===============================
    // MÉTODOS AVANZADOS
    // ===============================

    /**
     * Verifica múltiples permisos a la vez
     */
    hasPermissions(permissions: string[]): { [key: string]: boolean } {
        const result: { [key: string]: boolean } = {};

        permissions.forEach(permission => {
            switch (permission) {
                case 'manageUsers':
                    result[permission] = this.canManageUsers();
                    break;
                case 'viewReports':
                    result[permission] = this.canViewReports();
                    break;
                case 'viewSettings':
                    result[permission] = this.canViewSettings();
                    break;
                case 'manageInvoices':
                    result[permission] = this.canManageInvoices();
                    break;
                case 'deleteImportantRecords':
                    result[permission] = this.canDeleteImportantRecords();
                    break;
                default:
                    result[permission] = false;
            }
        });

        return result;
    }

    /**
     * Obtiene información completa del usuario para debugging
     */
    getUserInfo(): any {
        const user = this.getCurrentUser();
        if (!user) return null;

        return {
            user: user,
            permissions: {
                isAdmin: this.isAdmin(),
                canManageUsers: this.canManageUsers(),
                canViewReports: this.canViewReports(),
                canViewSettings: this.canViewSettings(),
                canManageInvoices: this.canManageInvoices(),
                canDeleteImportantRecords: this.canDeleteImportantRecords(),
                canViewFinancialData: this.canViewFinancialData(),
                canExportData: this.canExportData()
            }
        };
    }

    // ===============================
    // MÉTODO PARA ACTUALIZAR USUARIO (después de login)
    // ===============================

    /**
     * Actualiza el usuario después del login exitoso
     */
    updateUserFromLoginResponse(loginResponse: any): void {
        if (loginResponse && loginResponse.user) {
            const currentUser = this.transformUserData(loginResponse.user);
            this.setCurrentUser(currentUser);

            // También actualizar localStorage
            localStorage.setItem('user', JSON.stringify(loginResponse.user));
        }
    }
}